<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <vs-row
      vs-w="12"
      class="my-5 filter-container flex justify-between"
    >
      <vs-col vs-w="1">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col vs-w="3" />
      <vs-col
        vs-w="8"
        class="flex justify-end"
      >
        <div class="flex flex-row gap-x-4 items-stretch">
          <div class="flex items-center w-full">
            <select-with-multiple
              :hide-search="true"
              :is-busy="isBusy"
              v-model="filters.location_type"
              placeholder="Select Location Type"
              :options="locationTypesOptions"
              style="min-width: 260px;"
              :activate-search="true"
            />
          </div>
          <div class="flex items-center">
            <vs-button
              type="border"
              color="primary"
              class="self-center font-semibold"
              style="min-width: 100px"
              @click="resetFilters"
            >
              Reset
            </vs-button>
          </div>
        </div>
      </vs-col>
    </vs-row>
    <div class="p-5 flex flex-col mt-5 bg-white rounded-md">
      <table-top
        is-search-has-border
        :page-size-start="false"
        class="mt-5"
        @fetch="fetchLocations"
        ref="tabletop"
      >
        <template #start>
          <vs-button
            v-show="isUserCanAccessMenu(permission.name, 'add')"
            class="button shadow-primary font-semibold"
            color="primary"
            @click="startAdd"
          >
            Add New Location
          </vs-button>
          <vs-button
            class="button shadow-secondary font-semibold ml-5"
            color="secondary"
            @click="isDownload = true"
            :disabled="isDownload"
          >
            Export
          </vs-button>
        </template>
      </table-top>
      <data-table
        class="my-5"
        :loading="isBusy"
        :table-header="tableHeaders"
        :table-data="locationsData"
        :sort-by="filters.sort_by"
        body-height="auto"
        :sort-dir="filters.sort_dir"
        @sort="sort"
      >
        <template #no="{ index }">
          <div class="flex flex-row w-min">
            {{ (currentPage - 1) * pageSize + index + 1 }}
          </div>
        </template>
        <template #action="{ row }">
          <div class="flex flex-row gap-x-3">
            <div
              v-show="true"
              class="flex items-center justify-center icon-transparent-bg"
              @click="openLocationDetail(row)"
            >
              <img
                alt="see"
                :src="require('@/assets/shipo-icon-see.svg')"
                class="w-8 h-8"
              >
            </div>
            <div
              v-if="isUserCanAccessMenu(permission.name, 'edit')"
              class="flex items-center justify-center icon-transparent-bg"
              @click="startEdit(row)"
            >
              <img
                :src="require('@/assets/shipo-icon-edit.svg')"
                alt="edit"
              >
            </div>
            <div
              v-if="isUserCanAccessMenu(permission.name, 'delete')"
              class="flex items-center justify-center icon-transparent-bg"
              @click="startDelete(row)"
            >
              <img :src="require('@/assets/shipo-icon-delete.svg')">
            </div>
          </div>
        </template>
        <template #name="{ row }">
          {{ row.name }}
        </template>
        <template #type="{ row }">
          {{ row.type }}
        </template>
        <template #lat="{ row }">
          {{ row.lat }}
        </template>
        <template #long="{ row }">
          {{ row.long }}
        </template>
        <template #is_active="{ row }">
          <span
            :class="[
              (row.is_active === true) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800',
              'block w-full text-center py-2 rounded font-medium'
            ]"
          >
            {{ (row.is_active === true) ? 'Active' : 'Inactive' }}
          </span>
        </template>
      </data-table>
      <table-bottom @fetch="fetchLocations" />
    </div>
    <pop-up
      :active.sync="isEditLocationPopupShown"
      :title="isAdding ? 'Add New Location' : 'Edit Location'"
      hide-illustration
      hide-ok-button
      :is-busy="isProcessing"
      @close="isEditLocationPopupShown = false"
    >
      <div class="edit-location-form popup-content-size text-black mt-8">
        <div
          class="grid grid-cols-2 gap-6 mb-4"
        >
          <div>
            <label class="block mb-1 font-semibold">Location Name</label>
            <div class="relative">
              <vs-input
                class="w-full"
                type="text"
                placeholder="Location Name"
                v-model="editForm.name.value"
                :disabled="isProcessing"
              />
              <span
                v-if="!editForm.name.isValid"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </div>
          <div>
            <label class="block mb-1 font-semibold">Alias Name</label>
            <div class="relative">
              <vs-input
                class="w-full"
                type="text"
                placeholder="Alias Name"
                v-model="editForm.alias.value"
                :disabled="isProcessing"
              />
            </div>
          </div>
          <div>
            <label class="block mb-1 font-semibold">Type</label>
            <div class="relative">
              <select-with-search
                :hide-search="true"
                :options="typesOptions"
                v-model="editForm.type.value"
                :disabled="isProcessing"
              />
              <span
                v-if="!editForm.type.isValid"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </div>
          <div v-if="editForm.type.value === 'Port'">
            <label class="block mb-1 font-semibold">Port Type</label>
            <div class="relative">
              <select-with-search
                :hide-search="true"
                :options="portTypesOptions"
                v-model="editForm.type_port.value"
                :disabled="isProcessing"
              />
              <span
                v-if="editForm.type.value === 'Port' && !editForm.type_port.isValid"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </div>
          <div v-else>
            <br>
          </div>
          <div v-if="editForm.type.value === 'Port' && editForm.type_port.value === 'POD'">
            <label class="block mb-1 font-semibold">Location Color</label>
            <div class="relative flex items-center gap-3 py-1">
              <input
                class="input-field w-10 h-10 p-0 border-none"
                type="color"
                v-model="editForm.location_color.value"
                @input="onColorInput"
                style="min-width: 2.5rem; min-height: 2.5rem;"
                :disabled="isProcessing"
              >
              <vs-input
                class="w-40"
                type="text"
                v-model="editForm.location_color.value"
                placeholder="Hex (#FFFFFF)"
                :disabled="isProcessing"
              />
              <span
                v-if="editForm.type_port.value === 'POD' && !editForm.location_color.isValid"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
              <span
                v-else-if="editForm.location_color.value && (colorNotAllowed || !isColorAllowed)"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Color not Allowed</span>
            </div>
          </div>
          <div v-if="editForm.type.value === 'Port' && editForm.type_port.value === 'POD'">
            <br>
          </div>

          <div>
            <label class="block mb-1 font-semibold">Latitude</label>
            <div class="relative">
              <vs-input
                class="w-full"
                type="text"
                placeholder="-0.5102151"
                :value="editForm.lat.value"
                :disabled="isProcessing"
                @input="val => onHandleInput(val, 'lat')"
                @blur="() => onLatLongBlur('lat')"
              />
              <span
                v-if="!editForm.lat.isValid"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </div>
          <div>
            <label class="block mb-1 font-semibold">Longitude</label>
            <div class="relative">
              <vs-input
                class="w-full"
                type="text"
                placeholder="117.1222664"
                :value="editForm.long.value"
                :disabled="isProcessing"
                @input="val => onHandleInput(val, 'long')"
                @blur="() => onLatLongBlur('long')"
              />
              <span
                v-if="!editForm.long.isValid"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </div>
          <div class="mb-4 font-semibold">
            Status
            <vs-switch
              :disabled="isProcessing"
              v-model="editForm.is_active"
              color="secondary"
              class="mt-4"
            />
          </div>
        </div>
        <div 
          class="mb-6" 
          v-if="editForm.type.value !== 'Check Point' && editForm.type.value"
        >
          <div class="font-semibold text-primary mb-2">
            Weather & Water Device
          </div>
          <div class="grid grid-cols-2 gap-6 mb-4">
            <div>
              <label class="block mb-1 font-semibold">Weather Device ID</label>
              <vs-input
                class="w-full"
                type="text"
                placeholder="Weather Device ID"
                v-model="editForm.weather_device_id.value"
                :disabled="isProcessing"
              />
            </div>
            <div v-if="editForm.type.value === 'Port' || editForm.type.value === 'Bridge'">
              <label class="block mb-1 font-semibold">Water Level Device ID</label>
              <vs-input
                class="w-full"
                type="text"
                placeholder="Water Level Device ID"
                v-model="editForm.water_level_device_id.value"
                :disabled="isProcessing"
              />
            </div>
          </div>
          <div
            class="font-semibold mb-2"
            v-if="editForm.type.value === 'Port' || editForm.type.value === 'Bridge'"
          >
            Water Level Classification (in meters)
          </div>
          <div
            class="grid grid-cols-3 gap-6 mb-2 items-center"
            v-if="editForm.type.value === 'Port' || editForm.type.value === 'Bridge'"
          >
            <div class="flex items-center gap-2">
              <span
                class="w-6 h-6 rounded-full flex items-center justify-center"
                style="background:#6CA6AF;color:white;"
              >✔</span>
              <span>Safe Level</span>
            </div>
            <div class="flex flex-row items-center gap-2">
              <span class="text-secondary font-semibold">MIN</span>
              <vs-input
                class="w-full right-align-input"
                type="text"
                placeholder="Min"
                v-model="editForm.safe_min"
                :disabled="isProcessing"
                @input="onWaterLevelInput('safe_min')"
              />
            </div>
            <div class="flex flex-row items-center gap-2">
              <span class="text-secondary font-semibold">MAX</span>
              <vs-input
                class="w-full right-align-input"
                type="text"
                placeholder="Max"
                v-model="editForm.safe_max"
                :disabled="isProcessing"
                @input="onWaterLevelInput('safe_max')"
              />
            </div>
          </div>
          <div
            class="grid grid-cols-3 gap-6 mb-2 items-center"
            v-if="editForm.type.value === 'Port' || editForm.type.value === 'Bridge'"
          >
            <div class="flex items-center gap-2">
              <span   
                class="w-6 h-6 rounded-full flex items-center justify-center"   
                style="background:#FFB300;color:white;" 
              >!</span>
              <span>Caution Level</span>
            </div>
            <div class="flex flex-row items-center gap-2">
              <span class="text-secondary font-semibold">MIN</span>
              <vs-input
                class="w-full right-align-input"
                type="text"
                placeholder="Min"
                v-model="editForm.caution_min"
                :disabled="isProcessing"
                @input="onWaterLevelInput('caution_min')"
              />
            </div>
            <div class="flex flex-row items-center gap-2">
              <span class="text-secondary font-semibold">MAX</span>
              <vs-input
                class="w-full right-align-input"
                type="text"
                placeholder="Max"
                v-model="editForm.caution_max"
                :disabled="isProcessing"
                @input="onWaterLevelInput('caution_max')"
              />
            </div>
          </div>
          <div
            class="grid grid-cols-3 gap-6 mb-4 items-center"
            v-if="editForm.type.value === 'Port' || editForm.type.value === 'Bridge'"
          >
            <div class="flex items-center gap-2">
              <span 
                class="w-6 h-6 rounded-full flex items-center justify-center" 
                style="background:#F87171;color:white;" 
              >&#9888;</span>
              <span>Danger Level</span>
            </div>
            <div class="flex flex-row items-center gap-2">
              <span class="text-secondary font-semibold">MIN</span>
              <vs-input
                class="w-full right-align-input"
                type="text"
                placeholder="Min"
                v-model="editForm.danger_min"
                :disabled="isProcessing"
                @input="onWaterLevelInput('danger_min')"
              />
            </div>
            <div class="flex flex-row items-center gap-2">
              <span class="text-secondary font-semibold">MAX</span>
              <vs-input
                class="w-full right-align-input"
                type="text"
                placeholder="Max"
                v-model="editForm.danger_max"
                :disabled="isProcessing"
                @input="onWaterLevelInput('danger_max')"
              />
            </div>
          </div>
        </div>
        <!-- End Weather & Water Device Section -->
        <div class="mb-2 font-semibold text-primary">
          Location Radius
        </div>
        <div class="grid grid-cols-2 gap-6 mb-6">
          <div>
            <label class="block mb-1 font-semibold">Radius</label>
            <div class="flex items-center">
              <vs-input
                class="w-full"
                type="number"
                placeholder="Radius"
                :disabled="isProcessing"
                v-model="editForm.radius.value"
              />
              <span
                v-if="!editForm.radius.isValid"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
              <span class="ml-2 font-semibold text-secondary">KM</span>
            </div>
          </div>
          <div>
            <label class="block mb-1 font-semibold">Waiting Radius</label>
            <div class="flex items-center">
              <vs-input
                class="w-full"
                type="number"
                placeholder="Waiting Radius"
                :disabled="isProcessing"
                v-model="editForm.waiting_radius.value"
              />
              <span class="ml-2 font-semibold text-secondary">KM</span>
            </div>
          </div>
        </div>
        <div class="map-placeholder mb-5">
          <ArgisMapPopUp
            class="w-full h-64 rounded-lg"
            :layers="['location']"
          />
        </div>
        <div class="flex justify-between gap-4 mt-8">
          <button
            :class="`btn-cancel ${isProcessing && 'animate-bounce opacity-50 cursor-not-allowed'}`"
            :disabled="isProcessing"
            @click="isEditLocationPopupShown = false"
          >
            Cancel
          </button>
          <button
            :class="`btn-update ${isProcessing && 'animate-bounce opacity-50 cursor-not-allowed'}`"
            :disabled="isProcessing"
            @click="saveEditLocation"
          >
            {{ isAdding ? 'Add Location' : 'Update Location' }}
          </button>
        </div>
      </div>
    </pop-up>
    <pop-up
      :active.sync="isViewLocationPopupShown"
      :title="'View Location'"
      hide-illustration
      hide-ok-button
      :is-busy="isProcessing"
      @close="isViewLocationPopupShown = false"
    >
      <div
        class="view-location-form popup-content-size mt-8 text-black"
      >
        <div class="grid grid-cols-2 gap-x-16 gap-y-8 mb-8">
          <div>
            <div class="font-semibold mb-4">
              Location Name
            </div>
            <input
              class="input-field"
              type="text"
              :value="viewLocationData.name"
              readonly
            >
          </div>
          <div>
            <div class="font-semibold mb-4">
              Alias Name
            </div>
            <input
              class="input-field"
              type="text"
              :value="viewLocationData.alias"
              readonly
            >
          </div>
          <div>
            <div class="font-semibold mb-4">
              Type
            </div>
            <input
              class="input-field"
              type="text"
              :value="viewLocationData.type"
              readonly
            >
          </div>
          <div v-if="viewLocationData.type === 'Port'">
            <div class="font-semibold mb-4">
              Port Type
            </div>
            <input
              class="input-field"
              type="text"
              :value="viewLocationData.type_port"
              readonly
            >
          </div>
          <div v-else>
            <br>
          </div>
          <div v-if="viewLocationData.type === 'Port' && viewLocationData.type_port === 'POD'">
            <div class="font-semibold mb-4">
              Location Color
            </div>
            <div class="flex items-center gap-2">
              <input
                class="input-field w-10 h-10 p-0 border-none"
                type="color"
                :value="viewLocationData.location_color"
                readonly
                style="min-width: 2.5rem; min-height: 2.5rem;"
              >
              <input
                class="input-field w-40"
                type="text"
                :value="viewLocationData.location_color"
                readonly
              >
            </div>
          </div>
          <div v-if="viewLocationData.type === 'Port' && viewLocationData.type_port === 'POD'">
            <br>
          </div>
          <div>
            <div class="font-semibold mb-4">
              Center Latitude
            </div>
            <input
              class="input-field"
              type="text"
              :value="viewLocationData.lat"
              readonly
            >
          </div>
          <div>
            <div class="font-semibold mb-4">
              Center Longitude
            </div>
            <input
              class="input-field"
              type="text"
              :value="viewLocationData.long"
              readonly
            >
          </div>
          <div>
            <div class="font-semibold mb-4">
              Status
            </div>
            <vs-switch
              v-model="viewLocationData.is_active"
              color="secondary"
              disabled
            />
          </div>
          <div> <br> </div>
          <div v-if="viewLocationData.type !== 'Check Point'">
            <div class="font-semibold mb-4">
              Weather Device ID
            </div>
            <input
              class="input-field"
              type="text"
              :value="viewLocationData.weather_device_id"
              readonly
            >
          </div>
          <div v-if="viewLocationData.type !== 'Check Point'">
            <div class="font-semibold mb-4">
              Water Level Device ID
            </div>
            <input
              class="input-field"
              type="text"
              :value="viewLocationData.water_level_device_id"
              readonly
            >
          </div>
        </div>
        <div v-if="viewLocationData.type !== 'Check Point'">
          <div class="mb-4 font-semibold text-primary">
            Water Level Classification (in meters)
          </div>
          <div class="grid grid-cols-3 gap-6 mb-2 items-center">
            <div class="flex items-center gap-2">
              <span
                class="w-6 h-6 rounded-full flex items-center justify-center"
                style="background:#6CA6AF;color:white;"
              >✔</span>
              <span>Safe Level</span>
            </div>
            <div class="flex flex-row items-center gap-2">
              <span class="text-secondary font-semibold">MIN</span>
              <input
                class="input-field text-right"
                type="text"
                :value="(viewLocationData.water_level_classification && viewLocationData.water_level_classification[0].min) || '-'"
                readonly
              >
            </div>
            <div class="flex flex-row items-center gap-2">
              <span class="text-secondary font-semibold">MAX</span>
              <input
                class="input-field text-right"
                type="text"
                :value="(viewLocationData.water_level_classification && viewLocationData.water_level_classification[0].max) || '-'"
                readonly
              >
            </div>
          </div>
          <div class="grid grid-cols-3 gap-6 mb-2 items-center">
            <div class="flex items-center gap-2">
              <span
                class="w-6 h-6 rounded-full flex items-center justify-center"
                style="background:#FFB300;color:white;"
              >!</span>
              <span>Caution Level</span>
            </div>
            <div class="flex flex-row items-center gap-2">
              <span class="text-secondary font-semibold">MIN</span>
              <input
                class="input-field text-right"
                type="text"
                :value="(viewLocationData.water_level_classification && viewLocationData.water_level_classification[1].min) || '-'"
                readonly
              >
            </div>
            <div class="flex flex-row items-center gap-2">
              <span class="text-secondary font-semibold">MAX</span>
              <input
                class="input-field text-right"
                type="text"
                :value="(viewLocationData.water_level_classification && viewLocationData.water_level_classification[1].max) || '-'"
                readonly
              >
            </div>
          </div>
          <div class="grid grid-cols-3 gap-6 mb-4 items-center">
            <div class="flex items-center gap-2">
              <span
                class="w-6 h-6 rounded-full flex items-center justify-center"
                style="background:#F87171;color:white;"
              >&#9888;</span>
              <span>Danger Level</span>
            </div>
            <div class="flex flex-row items-center gap-2">
              <span class="text-secondary font-semibold">MIN</span>
              <input
                class="input-field text-right"
                type="text"
                :value="(viewLocationData.water_level_classification && viewLocationData.water_level_classification[2].min) || '-'"
                readonly
              >
            </div>
            <div class="flex flex-row items-center gap-2">
              <span class="text-secondary font-semibold">MAX</span>
              <input
                class="input-field text-right"
                type="text"
                :value="(viewLocationData.water_level_classification && viewLocationData.water_level_classification[2].max) || '-'"
                readonly
              >
            </div>
          </div>
        </div>
        <div class="mb-4 font-semibold text-primary">
          Location Radius
        </div>
        <div class="grid grid-cols-2 gap-6 mb-6">
          <div>
            <div class="font-semibold mb-4">
              Radius
            </div>
            <div class="flex justify-between">
              <input
                class="input-field w-full"
                type="text"
                :value="viewLocationData.radius"
                readonly
              >
              <span class="ml-2 font-semibold text-orange-400">KM</span>
            </div>
          </div>
          <div>
            <div class="font-semibold mb-4">
              Waiting Radius
            </div>
            <div class="flex justify-between">
              <input
                class="input-field w-full"
                type="text"
                :value="viewLocationData.waiting_radius"
                readonly
              >
              <span class="ml-2 font-semibold text-orange-400">KM</span>
            </div>
          </div>
        </div>
        <div class="map-placeholder mb-8">
          <ArgisMapPopUp
            class="w-full h-64 rounded-lg"
            :layers="['location']"
          />
        </div>
        <div class="flex justify-between gap-4 mt-8">
          <button
            class="btn-cancel"
            @click="isViewLocationPopupShown = false"
          >
            Close
          </button>
          <button
            v-if="isUserCanAccessMenu(permission.name, 'edit')"
            class="btn-update"
            @click="
              startEdit(viewLocationData)
              isViewLocationPopupShown = false;
            "
          >
            Edit Location
          </button>
        </div>
      </div>
    </pop-up>
    <pop-up
      :active.sync="popUp.isShown"
      is-warning
      title="Delete Location"
      left-button="Cancel"
      right-button="Yes, Delete"
      :is-busy="isProcessing"
      @close="popUp = { isShown: false, tempId: null, tempName: '' }"
      @leftButton="popUp = { isShown: false, tempId: null, tempName: '' }"
      @rightButton="deleteLocation(popUp.tempId)"
    >
      <p class="text-center mt-1">
        Are you sure want to delete location "{{ popUp.tempName }}"?
      </p>
      <p class="text-center">
        This action cannot be undone
      </p>
    </pop-up>
    <pop-up
      :active.sync="popUp.isShownRestore"
      is-restore
      is-warning
      title="Restore Location"
      left-button="Cancel"
      right-button="Restore"
      :is-busy="isProcessing"
      @close="closeRestoreModal"
      @leftButton="closeRestoreModal"
      @rightButton="restoreLocation"
    >
      <p class="text-center mt-1">
        Location {{ popUp.tempName }} already exists but deleted.
      </p>
      <p class="text-center">
        Would you like to restore?
      </p>
    </pop-up>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import ArgisMapPopUp from '@/components/arcgisMapPopUp'
import DataTable from '@/components/dataTable'
import PopUp from '@/components/popUp'
import SelectWithMultiple from '@/components/selectWithMultiple'
import SelectWithSearch from '@/components/selectWithSearch'
import TableBottom from '@/components/tableBottom'
import TableTop from '@/components/tableTop'
import { PAGE_TITLE, ZOOM } from '@/fixtures/Constants'
import EventBus from '@/plugins/EsriEventBus'
import downloadUtils from '@/utils/downloadUtils'


export default {
  components: {
    DataTable,
    TableBottom,
    TableTop,
    SelectWithMultiple,
    SelectWithSearch,
    PopUp,
    ArgisMapPopUp
  },
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  data() {
    return {
      isDownload: false,
      isBusy: false,
      isProcessing: false,
      filters: {
        sort_by: '',
        sort_dir: '',
        location_type: []
      },
      tableHeaders: [
        { key: 'no', label: 'No', width: '50px' },
        { key: 'action', label: 'Action', width: '120px' },
        { key: 'name', label: 'Name', sortable: true },
        { key: 'type', label: 'Type', sortable: true },
        { key: 'lat', label: 'Latitude', sortable: true },
        { key: 'long', label: 'Longitude', sortable: true },
        { key: 'is_active', label: 'Status', sortable: true }
      ],
      typesOptions: [
        { text: 'Port', value: 'Port' },
        { text: 'Bridge', value: 'Bridge' },
        { text: 'Check Point', value: 'Check Point' }
      ],
      portTypesOptions: [
        { text: 'POL', value: 'POL' },
        { text: 'POD', value: 'POD' }
      ],
      locationsData: [],
      locationTypesOptions: [],
      isEditLocationPopupShown: false,
      isAdding: false,
      isEditing: false,
      editForm: {
        name: { value: '', isValid: true },
        type: { value: '', isValid: true },
        lat: { value: '', isValid: true },
        long: { value: '', isValid: true },
        radius: { value: '', isValid: true },
        type_port: { value: '', isValid: true },
        location_color: { value: '#000000', isValid: true },
        alias: { value: '' },
        is_active: true,
        waiting_radius: { value: '' },
        weather_device_id: { value: '' },
        water_level_device_id: { value: '' },
        safe_min: '',
        safe_max: '',
        caution_min: '',
        caution_max: '',
        danger_min: '',
        danger_max: ''
      },
      isViewLocationPopupShown: false,
      viewLocationData: {},
      popUp: {
        isShown: false,
        isShownRestore: false,
        tempId: null,
        tempName: '',
        formData: null
      }
    }
  },
  computed: {
    ...mapGetters({
      locations: 'LocationManagement/locations',
      currentPage: 'Table/currentPage',
      pageSize: 'Table/pageSize',
      locationTypes: 'LocationManagement/locationTypes',
      locationFile: 'LocationManagement/locationFile',
      locationById: 'LocationManagement/locationById'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    },
    isColorAllowed() {
      return /^#[0-9A-Fa-f]{6}$/.test(this.editForm.location_color.value);
    },
    colorNotAllowed() {
      const notAllowedColors = ['#FFAA00', '#EE443F', '#4DA1A9'];
      const color = this.editForm.location_color?.value || '';
      return notAllowedColors.includes(color.toUpperCase());
    },
    waterLevelClassificationErrors() {
      const minMaxRegex = /^(>=|<=|>|<)?-?\d+(\.\d+)?$/;
      if (this.editForm.type.value === 'Check Point' || !Array.isArray(this.editForm.water_level_classification)) {
        return [false, false, false].map(() => ({ min: false, max: false }));
      }
      return this.editForm.water_level_classification.map(item => ({
        min: item.min !== '' && item.min !== null && !minMaxRegex.test(item.min),
        max: item.max !== '' && item.max !== null && !minMaxRegex.test(item.max)
      }));
    }
  },
  watch: {
    locations: {
      handler(val) {
        if (val) {
          this.locationsData = val.map(item => ({
            ...item,
            isEditing: false,
            editForm: null
          }))
        }
      }
    },
    locationById: {
      handler(val) {
        this.viewLocationData = val[0]
        if (this.isViewLocationPopupShown) {
          this.$nextTick(() => {
            this.emitMapGraphics(this.viewLocationData)
          })
        }
      }
    },
    locationTypes: {
      handler(val) {
        this.locationTypesOptions = val.map(item => ({ text: item.type, value: item.type }))
      }
    },
    'filters.location_type': {
      handler(val) {
        this.fetchLocations()
      }
    },
    isDownload: {
      handler(val) {
        if (!!val) {
          this.downloadLocations();
        }
      }
    },
    locationFile: {
      handler(val) {
        if (!this.isEmpty(val)) {
          downloadUtils(val, `Location-Management.xlsx`)
          this.$store.dispatch('LocationManagement/resetLocationFile');
        }
      }
    },
    'editForm.lat.value': {
      handler() {
        this.emitMapGraphics(this.editForm)
      }
    },
    'editForm.long.value': {
      handler() {
        this.emitMapGraphics(this.editForm)
      }
    },
    'editForm.radius.value': {
      handler() {
        this.emitMapGraphics(this.editForm)
      }
    },
    'editForm.waiting_radius.value': {
      handler() {
        this.emitMapGraphics(this.editForm)
      }
    },
    'editForm.type.value': {
      handler(newValue) {
        if (newValue !== 'Port') {
          this.editForm.type_port.value = ''
        }
        if (newValue !== 'Bridge' && newValue !== 'Port') {
          this.resetWaterLevelDevice()
        }
      }
    },
    'editForm.type_port.value': {
      handler(newValue) {
        if (newValue !== 'POL') {
          this.resetWaterLevelDevice()
        }
        if (newValue !== 'POD') {
          this.editForm.location_color.value = null
        }
      }
    }
  },
  mounted() {
    this.fetchLocations()
    this.fetchLocationTypes()
  },
  methods: {
    async fetchLocations() {
      try {
        this.isBusy = true
        const params = {
          sort_dir: this.filters.sort_dir || 'asc',
          page: this.currentPage || 1,
          limit: this.pageSize || 10
        }
        if (this.filters.location_type) {
          params.location_type = this.filters.location_type.map(item => item.value)
        }
        if (this.filters.sort_by) {
          params.sort_by = this.filters.sort_by
        }
        if (this.filters.sort_by) {
          params.sort_by = this.filters.sort_by
        }
        await this.$store.dispatch('LocationManagement/fetchLocations', params)
        this.isBusy = false
      } catch (error) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to fetch locations, please try again')
      }
    },
    async fetchLocationById(id) {
      try {
        await this.$store.dispatch('LocationManagement/fetchLocationById', id)
      } catch (error) {
        this.showFailedNotification(this, 'Failed to fetch location, please try again')
      }
    },
    async fetchLocationTypes() {
      try {
        await this.$store.dispatch('LocationManagement/fetchLocationTypes')
      } catch (error) {
        this.showFailedNotification(this, 'Failed to fetch location types, please try again')
      }
    },
    sort(sortBy, sortDir) {
      this.filters.sort_by = sortBy
      this.filters.sort_dir = sortDir
      this.fetchLocations()
    },
    resetFilters() {
      this.filters = {
        sort_by: '',
        sort_dir: '',
        location_type: []
      }
      this.fetchLocations()
      this.$refs.tabletop.$emit('reset-search')
    },
    startAdd() {
      this.isAdding = true;
      this.editForm = {
        name: { value: '', isValid: true },
        alias: { value: '' },
        type: { value: '', isValid: true },
        type_port: { value: '', isValid: true },
        lat: { value: '', isValid: true },
        long: { value: '', isValid: true },
        location_color: { value: '', isValid: true },
        is_active: true,
        radius: { value: '', isValid: true },
        waiting_radius: { value: '' },
        weather_device_id: { value: '' },
        water_level_device_id: { value: '' },
        safe_min: '',
        safe_max: '',
        caution_min: '',
        caution_max: '',
        danger_min: '',
        danger_max: ''
      };
      this.isEditLocationPopupShown = true;
      this.$nextTick(() => {
        this.emitMapGraphics(this.editForm)
      })
    },
    async startEdit(row) {
      this.isAdding = false;
      this.isProcessing = true;
      await this.fetchLocationById(row.id)
      const wlc = Array.isArray(this.viewLocationData.water_level_classification)
        ? this.viewLocationData.water_level_classification
        : [];
      this.editForm = {
        name: { value: this.viewLocationData.name, isValid: true },
        alias: { value: this.viewLocationData.alias},
        type: { value: this.viewLocationData.type, isValid: true },
        type_port: { value: this.viewLocationData.type_port, isValid: true },
        lat: { value: this.viewLocationData.lat, isValid: true },
        long: { value: this.viewLocationData.long, isValid: true },
        location_color: { value: this.viewLocationData.location_color || '', isValid: true },
        is_active: this.viewLocationData.is_active,
        radius: { value: this.viewLocationData.radius, isValid: true },
        waiting_radius: { value: this.viewLocationData.waiting_radius },
        weather_device_id: { value: this.viewLocationData.weather_device_id },
        water_level_device_id: { value: this.viewLocationData.water_level_device_id },
        safe_min: wlc[0]?.min || '',
        safe_max: wlc[0]?.max || '',
        caution_min: wlc[1]?.min || '',
        caution_max: wlc[1]?.max || '',
        danger_min: wlc[2]?.min || '',
        danger_max: wlc[2]?.max || ''
      };
      this.isProcessing = false;
      this.isEditLocationPopupShown = true
      this.$nextTick(() => {
        this.emitMapGraphics(this.editForm)
      })
    },
    startDelete(row) {
      this.popUp = {
        isShown: true,
        tempId: row.id,
        tempName: row.name
      }
    },
    async openLocationDetail(row) {
      await this.fetchLocationById(row.id)
      this.isViewLocationPopupShown = true
      this.$nextTick(() => {
        this.emitMapGraphics(this.viewLocationData)
      })
    },
    validateEditForm(form) {
      Object.keys(form).forEach(key => {
        if (form[key] && form[key].hasOwnProperty('isValid')) {
          if (key === 'type_port' && form.type.value !== 'Port') {
            form[key].isValid = true;
          } else if (key === 'location_color') {
            form[key].isValid = form.type.value === 'Port' && form.type_port.value === 'POD' 
              ? form[key].value !== '' && form[key].value !== null && form[key].value !== undefined
              : true;
          } else if (['lat', 'long', 'radius'].includes(key)) {
            form[key].isValid = form[key].value !== '' && form[key].value !== null && form[key].value !== undefined;
          } else if (key === 'location_color' && form.type.value === 'Port' && form.type_port.value === 'POD') {
            form[key].isValid = !!form[key].value && form[key].value !== '#FFFFFF';
          } else {
            form[key].isValid = !!form[key].value;
          }
        }
      });
      return Object.keys(form).some(key =>
        form[key] && form[key].hasOwnProperty('isValid')
          ? !form[key].isValid
          : false
      );
    },
    isColorUnique(color, currentId = null) {
      const colorToCheck = (color || '').trim().toUpperCase();
      const result = !this.locationsData.some(loc =>
        (loc.type === 'Port-POD') &&
        (loc.type_port === 'POD') &&
        loc.location_color &&
        loc.location_color.trim().toUpperCase() === colorToCheck &&
        (!currentId || loc.id !== currentId)
      );
      return result
    },
    // isDeviceIdUnique(field, value, currentId = null) {
    //   const idToCheck = (value || '').trim().toLowerCase();
    //   if (!idToCheck) return true;
    //   return !this.locationsData.some(loc =>
    //     loc[field] &&
    //     loc[field].trim().toLowerCase() === idToCheck &&
    //     (!currentId || loc.id !== currentId)
    //   );
    // },
    async saveEditLocation() {
      try {
        if (
          this.editForm.type.value === 'Port' &&
          this.editForm.type_port.value === 'POD' &&
          (this.colorNotAllowed || !this.isColorAllowed)
        ) {
          this.showFailedNotification(
            this,
            'Color not allowed. Please choose a different color.',
            'Failed'
          );
          return;
        }
        if (
          this.editForm.type.value === 'Port' &&
          this.editForm.type_port.value === 'POD' &&
          !this.isColorUnique(this.editForm.location_color.value, this.viewLocationData?.id)
        ) {
          this.showFailedNotification(
            this,
            'Selected color already exists/reserved. Please choose another color.',
            'Failed'
          );
          return;
        }
        if (this.validateEditForm(this.editForm)) {
          this.showFailedNotification(
            this,
            'Some fields is empty, please fill it first',
            'Failed'
          )
          return
        }
        if (this.waterLevelClassificationErrors.some(e => e.min || e.max)) {
          this.showFailedNotification(
            this,
            'Max & MinOnly Allow Number and Operator Symbol (>, <, >=, <=)',
            'Failed'
          )
          return
        }
        const payload = {
          name: this.editForm.name.value,
          type: this.editForm.type.value,
          lat: this.editForm.lat.value,
          long: this.editForm.long.value,
          radius: this.editForm.radius.value,
          is_active: this.editForm.is_active
        };

        if (this.editForm.alias.value) {
          payload.alias = this.editForm.alias.value;
        }
        if (this.editForm.waiting_radius.value) {
          payload.waiting_radius = this.editForm.waiting_radius.value;
        }
        if (this.editForm.location_color.value) {
          payload.location_color = this.editForm.location_color.value;
        }
        if (this.editForm.type.value === 'Port') {
          payload.type_port = this.editForm.type_port.value;
          if (this.editForm.type_port.value === 'POD' && this.editForm.location_color.value) {
            payload.location_color = this.editForm.location_color.value;
          }
        } else {
          payload.type_port = null;
        }
        if (this.editForm.type.value === 'Port' || this.editForm.type.value === 'Bridge') {
          payload.weather_device_id = this.editForm.weather_device_id.value || '';
          payload.water_level_device_id = this.editForm.water_level_device_id.value || '';
          payload.water_level_classification = [
            {
              name: 'SAFE',
              min: this.editForm.safe_min || '',
              max: this.editForm.safe_max || ''
            },
            {
              name: 'CAUTION',
              min: this.editForm.caution_min || '',
              max: this.editForm.caution_max || ''
            },
            {
              name: 'DANGER',
              min: this.editForm.danger_min || '',
              max: this.editForm.danger_max || ''
            }
          ];
        }
        
        const nameExists = this.locationsData.some(loc => 
          loc.name.toLowerCase() === payload.name.trim().toLowerCase() && 
          (!this.viewLocationData.id || loc.id !== this.viewLocationData.id)
        )
        if (nameExists) {
          this.showFailedNotification(this, `Location "${payload.name}" already exists.`)
          return;
        }
        this.isProcessing = true
        const isRestoreData = await this.checkRestoreData(payload)
        if(isRestoreData) return;
        
        if (this.isAdding) {
          await this.$store.dispatch('LocationManagement/addLocation', { form: payload })
          this.showSuccessNotification(this, 'Location successfully added')
        } else {
          await this.$store.dispatch('LocationManagement/editLocation', { id: this.viewLocationData.id, form: payload })
          this.showSuccessNotification(this, 'Location successfully updated')
        }
        this.isEditLocationPopupShown = false
        this.isProcessing = false
        this.fetchLocations()
      } catch (error) {
        this.isProcessing = false
        this.showFailedNotification(this, error?.errors || error?.message || error?.error?.errors || error?.error?.message || 'Failed to save location, please try again')
      }
    },
    async deleteLocation(id) {
      try {
        await this.$store.dispatch('LocationManagement/deleteLocation', id)
        this.showSuccessNotification(this, 'Location successfully deleted')
        this.fetchLocations()
        this.popUp.isShown = false
      } catch ({ error }) {
        this.showFailedNotification(this, error?.errors || error?.message || 'Failed to delete location, please try again')
      }
    },
    closeRestoreModal() {
      this.popUp = {
        isShown: false,
        isShownRestore: false,
        tempId: null,
        tempName: '',
        formData: null
      }
    },
    async restoreLocation() {
      try {
        const formData = {
          ...this.popUp.formData,
          is_restore: true
        }
        await this.$store.dispatch('LocationManagement/editLocation', { id: this.popUp.tempId, form: formData })
        this.showSuccessNotification(this, 'Location successfully restored')
        this.fetchLocations()
        this.closeRestoreModal()
      } catch ({ error }) {
        this.showFailedNotification(this, error?.errors || error?.message || 'Failed to restore location, please try again')
      }
    },
    async checkRestoreData(payload) {
      try {
        const data = await this.$store.dispatch('LocationManagement/fetchLocations', { name: payload.name, is_get_deleted: true })
        if (data) {
          const location = data
          if (location.name === payload.name) {
            this.popUp = {
              ...this.popUp,
              isShownRestore: true,
              tempId: location.id,
              tempName: location.name,
              formData: payload
            }
            this.isEditLocationPopupShown = false
            return true
          }
        }
        return false
      } catch (error) {
        this.showFailedNotification(this, error?.message || 'Failed to check restore data, please try again')
        return false
      }
    },
    async downloadLocations() {
      this.isBusy = true;
      try {
        const apiFilters = {
          sort_dir: this.filters.sort_dir || 'asc',
          location_type: this.filters.location_type.map(item => item.value)
        };
        if (this.filters.sort_by) {
          apiFilters.sort_by = this.filters.sort_by
        }
        await this.$store.dispatch('LocationManagement/downloadLocations', apiFilters, {
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          },
          responseType: 'blob'
        });
        this.isBusy = false;
        this.isDownload = false;
      } catch (error) {
        this.isBusy = false;
        this.isDownload = false;
        this.showFailedNotification(this, 'Failed to download locations, please try again');
      }
    },
    emitMapGraphics(data) {
      const lat = parseFloat(data.lat?.value ?? data.lat)
      const lng = parseFloat(data.long?.value ?? data.long)
      const radius = parseFloat(data.radius?.value ?? data.radius)
      const waitingRadius = parseFloat(data.waiting_radius?.value ?? data.waiting_radius)
      if (isNaN(lat) || isNaN(lng)) return
      const graphics = []
      // Marker
      graphics.push({
        geometry: {
          type: 'point',
          latitude: lat,
          longitude: lng
        },
        symbol: {
          type: 'simple-marker',
          color: '#7c3aed',
          size: 12,
          outline: {
            color: '#fff',
            width: 2
          }
        }
      })
      // Main radius
      if (radius) {
        graphics.push({
          geometry: {
            type: 'circle',
            center: [lng, lat],
            radius: radius * 1000
          },
          symbol: {
            type: 'simple-fill',
            color: [124, 58, 237, 0.15],
            outline: {
              color: [124, 58, 237, 1],
              width: 2
            }
          }
        })
      }
      // Waiting radius
      if (waitingRadius) {
        graphics.push({
          geometry: {
            type: 'circle',
            center: [lng, lat],
            radius: waitingRadius * 1000
          },
          symbol: {
            type: 'simple-fill',
            color: [59, 130, 246, 0.10],
            outline: {
              color: [59, 130, 246, 1],
              width: 2
            }
          }
        })
      }
      EventBus.$emit('map-remove-graphics', { layerIds: ['location'] })
      EventBus.$emit('map-add-areas', { layerId: 'location', graphics })
      EventBus.$emit('map-set-view', { center: [lng, lat], zoom: ZOOM })
    },
    onColorInput(event) {
      let color;
      if (event && event.target && event.target.value) {
        color = event.target.value.toUpperCase();
      } else if (typeof event === 'string') {
        color = event;
      } else {
        color = '#000000';
      }
      if (color && !/^#[0-9A-Fa-f]{6}$/.test(color)) {
        this.editForm.location_color.value = '#000000';
      } else {
        this.editForm.location_color.value = color.toUpperCase();
      }
    },
    onWaterLevelInput(field) {
      // Replace comma with dot as user types
      if (typeof this.editForm[field] === 'string') {
        this.editForm[field] = this.editForm[field].replace(/,/g, '.');
      }
    },
    resetWaterLevelDevice() {
      this.editForm.water_level_device_id.value = '';
      this.editForm.safe_min = '';
      this.editForm.safe_max = '';
      this.editForm.caution_min = '';
      this.editForm.caution_max = '';
      this.editForm.warning_min = '';
      this.editForm.warning_max = '';
    },
    onHandleInput(val, type) {
      // Prepend 0 if value starts with '.'
      if (val.startsWith('.')) val = `0${val}`;
      if (val.startsWith('-.')) val = `-0${val.slice(1)}`;

      // Allow empty string temporarily
      if (val === '') {
        this.editForm[type].value = '';
        return;
      }

      // Allow partial valid input (not a valid number yet)
      if (
        val === '-' ||
        val === '-0' ||
        val === '-0.' ||
        val === '.' ||
        val === '-.' ||
        /^-?\d*\.$/.test(val)
      ) {
        this.editForm[type].value = val;
        return;
      }

      // Try to parse to float
      let parsed = parseFloat(val);

      // If not a number, don't update
      if (isNaN(parsed)) return;

      // Clamp value
      if (type === 'lat') {
        if (parsed < -90) parsed = -90;
        if (parsed > 90) parsed = 90;
      } else {
        if (parsed < -180) parsed = -180;
        if (parsed > 180) parsed = 180;
      }

      this.editForm[type].value = parsed
    },
    onLatLongBlur(type) {
      const val = this.editForm[type].value;
      // If the value is exactly '-0', '-0.', or '-0.0', set to 0
      if (val === '-0' || val === '-0.' || val === '-0.0' || val === '-') {
        this.editForm[type].value = '0';
      }
      // Optionally, you can also handle cases like '-0.00', etc.
      else if (/^-0\.0*$/.test(val)) {
        this.editForm[type].value = '0';
      }
    }
  }
}
</script>

<style scoped>
.filter-container {
  @apply w-full p-3 mt-5 flex flex-row items-center bg-white rounded-lg;
}

.icon-transparent-bg {
  @apply cursor-pointer;
  @apply min-w-8;
  @apply h-8;
  @apply bg-primary;
  border-radius: 0.5rem;

  &.complete {
    background-color: #D1F9E3;
  }

  &.delete {
    background-color: rgba(255, 99, 123, 0.16);
  }
}

.input-field {
  width: 100%;
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  background: #f6f6fa;
  font-size: 1rem;
}

.btn-cancel {
  background: #fff;
  color: #7c3aed;
  border: 2px solid #7c3aed;
  border-radius: 0.5rem;
  padding: 0.75rem 2rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  width: 100%;
}
.btn-cancel:hover {
  background: #f3e8ff;
}
.btn-update {
  background: #7c3aed;
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 2rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;
  width: 100%;
}
.btn-update:hover {
  background: #5b21b6;
}
.view-location-form .font-semibold {
  font-weight: 600;
}
.text-primary {
  color: #7c3aed;
}
.text-orange-400 {
  color: #ff7a59;
}
.popup ::-webkit-scrollbar {
  display: none;
}
.popup {
  scrollbar-width: none; /* Firefox */
}

::v-deep .right-align-input input {
  text-align: right;
}

.popup-content-size {
  width: 600px;
}
</style>
